import { TeamState, SetRecord, MATCH_CONFIG, Timeout } from '@/types';

interface ScoringHeaderProps {
  teamA: TeamState;
  teamB: TeamState;
  currentSetNumber: number;
  matchType: 'league' | 'playoff';
  currentScoreA: number;
  currentScoreB: number;
  completedSets: SetRecord[];
  compact?: boolean;
  servingTeamId?: string | null;
  completedTimeouts?: Timeout[];
}

export default function ScoringHeader({
  teamA,
  teamB,
  currentSetNumber,
  matchType,
  currentScoreA,
  currentScoreB,
  completedSets,
  compact = false,
  servingTeamId = null,
  completedTimeouts = [],
}: ScoringHeaderProps) {
  const maxSets = MATCH_CONFIG[matchType].maxSets;

  // Serving team's score is displayed first during active scoring.
  const servingFirst = servingTeamId === teamB.id;
  const firstTeam = servingFirst ? teamB : teamA;
  const secondTeam = servingFirst ? teamA : teamB;
  const firstScore = servingFirst ? currentScoreB : currentScoreA;
  const secondScore = servingFirst ? currentScoreA : currentScoreB;

  // Timeout counts per team
  const firstTeamTimeouts = completedTimeouts.filter(t => t.teamId === firstTeam.id).length;
  const secondTeamTimeouts = completedTimeouts.filter(t => t.teamId === secondTeam.id).length;

  const renderTeamLogo = (team: TeamState, size: 'sm' | 'md' = 'sm') => {
    const sizeClasses = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
    if (team.logoUrl) {
      return (
        <img
          src={team.logoUrl}
          alt={`${team.shortName} logo`}
          className={`${sizeClasses} rounded object-contain bg-surface p-0.5`}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const fallback = e.currentTarget.nextElementSibling;
            if (fallback) {
              (fallback as HTMLElement).style.display = 'flex';
            }
          }}
        />
      );
    }
    return (
      <div
        className={`${sizeClasses} rounded flex items-center justify-center flex-shrink-0`}
        style={{ backgroundColor: team.color }}
      >
        <span className="text-white text-xs font-bold">{team.shortName?.charAt(0) || '?'}</span>
      </div>
    );
  };

  if (compact) {
    return (
      <div className="bg-surface-raised border border-border rounded-lg p-2 mb-3">
        {/* Single row: Set info + Score */}
        <div className="flex items-center justify-between gap-2">
          {/* Current Set */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-serving animate-pulse" />
            <span className="text-xs font-bold text-text-primary uppercase whitespace-nowrap">
              SET {currentSetNumber}/{maxSets}
            </span>
          </div>

          {/* Current Score — serving team first */}
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] font-bold text-white flex-shrink-0"
              title="Timeouts taken"
            >
              T{firstTeamTimeouts}
            </span>
            <span className="font-mono-nums text-lg font-bold" style={{ color: firstTeam.color }}>
              {firstScore}
            </span>
            <span className="text-xs font-bold uppercase" style={{ color: firstTeam.color }}>
              {firstTeam.shortName}
            </span>
            <span className="text-text-muted text-sm">-</span>
            <span className="text-xs font-bold uppercase" style={{ color: secondTeam.color }}>
              {secondTeam.shortName}
            </span>
            <span className="font-mono-nums text-lg font-bold" style={{ color: secondTeam.color }}>
              {secondScore}
            </span>
            <span
              className="text-[10px] font-bold text-white flex-shrink-0"
              title="Timeouts taken"
            >
              T{secondTeamTimeouts}
            </span>
          </div>

          {/* Sets Won */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="font-bold text-sm" style={{ color: teamA.color }}>{teamA.setsWon}</span>
            <span className="text-text-muted text-xs">-</span>
            <span className="font-bold text-sm" style={{ color: teamB.color }}>{teamB.setsWon}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-raised border border-border rounded-lg p-3 mb-4">
      {/* Current Set Header */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="w-2 h-2 rounded-full bg-serving animate-pulse" />
        <span className="text-sm font-bold text-text-primary uppercase tracking-wide">
          SET {currentSetNumber} OF {maxSets}
        </span>
      </div>

      {/* Sets Won Summary */}
      <div className="flex items-center justify-center gap-4 mb-3 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          {renderTeamLogo(teamA, 'md')}
          <div className="flex gap-1">
            {Array.from({ length: maxSets }).map((_, i) => (
              <span
                key={i}
                className={`w-4 h-4 rounded-full ${
                  i < teamA.setsWon ? 'bg-team-a' : 'bg-border'
                }`}
                style={i < teamA.setsWon ? { backgroundColor: teamA.color } : {}}
              />
            ))}
          </div>
          <span className="text-sm font-bold" style={{ color: teamA.color }}>
            {teamA.shortName}
          </span>
        </div>
        <span className="text-text-muted text-sm font-medium">
          {teamA.setsWon} - {teamB.setsWon}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: teamB.color }}>
            {teamB.shortName}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: maxSets }).map((_, i) => (
              <span
                key={i}
                className={`w-4 h-4 rounded-full ${
                  i < teamB.setsWon ? 'bg-team-b' : 'bg-border'
                }`}
                style={i < teamB.setsWon ? { backgroundColor: teamB.color } : {}}
              />
            ))}
          </div>
          {renderTeamLogo(teamB, 'md')}
        </div>
      </div>

      {/* Current Set Score — serving team first */}
      <div className="flex items-center justify-center gap-6 mb-3">
        <div className="text-center">
          <div className="flex items-center gap-2 mb-1">
            {/* Timeout count at outer left edge */}
            <span
              className="text-xs font-bold text-white flex-shrink-0"
              title="Timeouts taken"
            >
              T{firstTeamTimeouts}
            </span>
            {renderTeamLogo(firstTeam)}
            <span className="text-xs font-semibold uppercase" style={{ color: firstTeam.color }}>
              {firstTeam.shortName}
            </span>
          </div>
          <span className="font-mono-nums text-3xl font-bold text-text-primary">
            {String(firstScore).padStart(2, '0')}
          </span>
        </div>
        <span className="text-text-muted text-2xl">-</span>
        <div className="text-center">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase" style={{ color: secondTeam.color }}>
              {secondTeam.shortName}
            </span>
            {renderTeamLogo(secondTeam)}
            {/* Timeout count at outer right edge */}
            <span
              className="text-xs font-bold text-white flex-shrink-0"
              title="Timeouts taken"
            >
              T{secondTeamTimeouts}
            </span>
          </div>
          <span className="font-mono-nums text-3xl font-bold text-text-primary">
            {String(secondScore).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* Completed Sets */}
      {completedSets.length > 0 && (
        <div className="pt-3 border-t border-border">
          <p className="text-[10px] font-semibold uppercase text-text-muted mb-2 text-center">
            Completed Sets
          </p>
          <div className="space-y-1">
            {completedSets
              .filter(s => s.winnerId !== null)
              .sort((a, b) => a.setNumber - b.setNumber)
              .map(set => {
                return (
                  <div
                    key={set.id}
                    className="flex items-center justify-center gap-2 text-xs"
                  >
                    <span className="text-success">✓</span>
                    <span className="text-text-muted">Set {set.setNumber}:</span>
                    <span className="font-bold" style={{ color: teamA.color }}>
                      {teamA.shortName} {set.scoreA}
                    </span>
                    <span className="text-text-muted">-</span>
                    <span className="font-bold" style={{ color: teamB.color }}>
                      {teamB.shortName} {set.scoreB}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
